import { Mark, mergeAttributes } from '@tiptap/core'

/**
 * The text colour of a prose part (AB#287): a mark of its own, independent of the highlight
 * (highlight-mark.ts) — changing one never touches the other.
 *
 * WHY A MARKDOWN SYNTAX. A part IS its markdown, and tiptap-markdown runs with `html: false`, so a
 * `<span style="color:…">` would be dropped on the next serialisation. The colour is written as a
 * pandoc bracketed span:
 *
 *   [words]{color=red}        a named colour of TEXT_COLOURS
 *   [words]{color=#C00000}    any other colour, as #RRGGBB
 *
 * It is never a link: a link's `]` is followed by `(`, never by `{color=`, and the rule runs before
 * markdown-it's link rule. The opener is the fixed string `[`, so tiptap-markdown's whitespace
 * re-flow of an opening delimiter (see the WHY note in highlight-mark.ts) cannot break it; the
 * colour rides on the closer, as the highlight's does.
 *
 * PARITY. `backend/adapters/markdown/text-colour-markdown.ts` is the same extension for the server
 * canonicaliser — both must produce the same bytes, which the `color-*` cases of
 * `shared/md-canonical.fixture.json` prove. The exports read the same syntax
 * (backend/domain/text-colour.ts).
 */

/** The prose text palette, by name: drawn by the design tokens of the same colours (styles.css). */
export const TEXT_COLOURS = ['blue', 'navy', 'green', 'red', 'purple', 'amber', 'grey'] as const
export type TextColourName = (typeof TEXT_COLOURS)[number]

/** A text colour as stored: a named colour, or a custom one as `#RRGGBB` (upper case). */
export type TextColour = TextColourName | `#${string}`

/** Each named colour's token — the editor draws it, and the colour panel shows it, with that. */
export const TEXT_COLOUR_TOKEN: Record<TextColourName, string> = {
  blue: 'var(--primary)',
  navy: 'var(--info-ink)',
  green: 'var(--green)',
  red: 'var(--red)',
  purple: 'var(--purple)',
  amber: 'var(--amber)',
  grey: 'var(--muted)',
}

export const isTextColourName = (v: unknown): v is TextColourName =>
  typeof v === 'string' && (TEXT_COLOURS as readonly string[]).includes(v)

/** `v` as a text colour (a name, or `#rrggbb` upper-cased), or null when it is neither. */
export function toTextColour(v: unknown): TextColour | null {
  if (isTextColourName(v)) return v
  return typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? (v.toUpperCase() as TextColour) : null
}

/** The markdown closer of a coloured span: `]{color=…}`. */
export const textColourClose = (colour: unknown): string => `]{color=${toTextColour(colour) ?? TEXT_COLOURS[0]}}`

/** The DOM attribute the markdown-it rule hands the colour over in. */
const COLOUR_ATTR = 'data-color'

/** Minimal structural view of markdown-it — just what the inline rule touches. */
interface MdToken {
  attrs: Array<[string, string]> | null
  markup: string
}
interface MdInlineState {
  src: string
  pos: number
  posMax: number
  md: { inline: { tokenize(state: MdInlineState): void } }
  push(type: string, tag: string, nesting: number): MdToken
}
interface MdIt {
  inline: { ruler: { before(before: string, name: string, rule: (state: MdInlineState, silent: boolean) => boolean): void } }
}

/** tiptap-markdown runs every extension's `parse.setup` before EVERY parse, on one long-lived
 *  markdown-it instance — register the rule once per instance. */
const withRule = new WeakSet<object>()

/** Past a code span opening at `i` (a run of backticks), or just past the run when it never closes. */
function skipCode(src: string, i: number, max: number): number {
  let run = 1
  while (src[i + run] === '`') run++
  const end = src.indexOf('`'.repeat(run), i + run)
  return end >= 0 && end + run <= max ? end + run : i + run
}

/**
 * The markdown-it inline rule: `[content]{color=value}`. Brackets nest, escapes and code spans are
 * skipped when looking for the closer; an unknown value is no span (the brackets are left to the
 * link rule, or stay text).
 */
export function textColourRule(state: MdInlineState, silent: boolean): boolean {
  const { src, pos: start, posMax: max } = state
  if (src[start] !== '[') return false
  const from = start + 1
  let depth = 1
  let close = -1
  for (let i = from; i < max; ) {
    const c = src[i]
    if (c === '\\') {
      i += 2
      continue
    }
    if (c === '`') {
      i = skipCode(src, i, max)
      continue
    }
    if (c === '[') depth++
    else if (c === ']' && --depth === 0) {
      close = i
      break
    }
    i++
  }
  if (close <= from) return false
  const attr = /^\{color=([a-z]+|#[0-9a-fA-F]{6})\}/.exec(src.slice(close + 1, max))
  const colour = attr ? toTextColour(attr[1]) : null
  if (!attr || !colour) return false
  if (!silent) {
    const open = state.push('niobe_color_open', 'span', 1)
    open.attrs = [[COLOUR_ATTR, colour]]
    open.markup = '['
    state.pos = from
    state.posMax = close
    state.md.inline.tokenize(state)
    state.posMax = max
    state.push('niobe_color_close', 'span', -1).markup = ']'
  }
  state.pos = close + 1 + attr[0].length
  return true
}

export const ProseTextColour = Mark.create({
  name: 'textColour',
  // Ranked OUTSIDE every other mark, the link (1000) included: markdown-it refuses a link label
  // that holds another `[…]` token (no links in links), so a coloured link must be written
  // `[[label](url)]{color=red}`, never `[[label]{color=red}](url)`. A span over a highlight or over
  // `**bold** and *em*` is then one `[…]{color=…}` around them.
  priority: 1100,

  addAttributes() {
    return {
      color: {
        default: TEXT_COLOURS[0],
        parseHTML: (el: HTMLElement) => toTextColour(el.getAttribute(COLOUR_ATTR)) ?? TEXT_COLOURS[0],
        // A named colour is drawn by its token (styles.css), so it follows the colour scheme; a
        // custom one inline.
        renderHTML: (attrs: { color?: unknown }) => {
          const v = toTextColour(attrs.color) ?? TEXT_COLOURS[0]
          return v.startsWith('#') ? { [COLOUR_ATTR]: v, style: `color:${v}` } : { [COLOUR_ATTR]: v }
        },
      },
    }
  },

  parseHTML() {
    return [{ tag: `span[${COLOUR_ATTR}]` }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0]
  },

  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '[',
          close: (_state: unknown, mark: { attrs: { color?: unknown } }) => textColourClose(mark.attrs.color),
          mixable: true,
          expelEnclosingWhitespace: true,
        },
        parse: {
          setup(markdownit: MdIt) {
            if (withRule.has(markdownit)) return
            withRule.add(markdownit)
            markdownit.inline.ruler.before('link', 'niobe_color', textColourRule)
          },
        },
      },
    }
  },
})
